import type { VideoSubscriptionPlan } from "@/hooks/useVideoSubscriptionPlans";
import { useDisplayRegion, type Region } from "@/lib/region";

export type DisplayCurrency = "USD" | "MXN" | "COP";

const REGION_CURRENCY: Record<Region, DisplayCurrency> = {
  US: "USD",
  MX: "MXN",
  CO: "COP",
};

/**
 * Currency tracks the display REGION (country switcher / geolocation),
 * no longer the language alone: Colombia uses the Spanish UI with COP
 * prices. If a plan doesn't have a price configured in the active
 * currency, we fall back to USD so visitors always see something they
 * can buy.
 */
export function useDisplayCurrency(): DisplayCurrency {
  const region = useDisplayRegion();
  return REGION_CURRENCY[region];
}

/** Resolve {amount, currency} for a plan in the active currency, with USD fallback. */
export function resolvePlanPrice(
  plan: Pick<VideoSubscriptionPlan, "price_amount" | "price_amount_mxn" | "price_amount_cop">,
  currency: DisplayCurrency,
): { amount: number; currency: DisplayCurrency } {
  if (currency === "MXN" && plan.price_amount_mxn != null) {
    return { amount: Number(plan.price_amount_mxn), currency: "MXN" };
  }
  if (currency === "COP" && plan.price_amount_cop != null) {
    return { amount: Number(plan.price_amount_cop), currency: "COP" };
  }
  return { amount: Number(plan.price_amount), currency: "USD" };
}

/** Resolve the optional "compare at" (original) price in the active currency. */
export function resolveCompareAtPrice(
  plan: Pick<VideoSubscriptionPlan, "compare_at_amount" | "compare_at_amount_mxn" | "compare_at_amount_cop">,
  currency: DisplayCurrency,
): { amount: number; currency: DisplayCurrency } | null {
  if (currency === "MXN" && plan.compare_at_amount_mxn != null) {
    return { amount: Number(plan.compare_at_amount_mxn), currency: "MXN" };
  }
  if (currency === "COP" && plan.compare_at_amount_cop != null) {
    return { amount: Number(plan.compare_at_amount_cop), currency: "COP" };
  }
  if (plan.compare_at_amount != null) {
    return { amount: Number(plan.compare_at_amount), currency: "USD" };
  }
  return null;
}

export function formatPlanPrice(amount: number, currency: DisplayCurrency): string {
  if (currency === "COP") {
    // COP amounts are large and never carry cents — group the thousands with
    // commas ($45,000 COP): the es-CO dot grouping reads like a decimal.
    return `$${Math.round(amount).toLocaleString("en-US")} COP`;
  }
  const display = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
  return currency === "MXN" ? `$${display} MXN` : `$${display}`;
}
